﻿import { Ignore } from '../decorators/inject-decorator';
import { Kb3dModel } from '../decorators/kb3d-decorator';
import { IKbQuaternion } from '../kb3d-models';
import { Token } from '../token';
import { KbVector } from './kb-vector';

export interface IKbQuaternionLike {
    x: number;
    y: number;
    z: number;
    w: number;
}

@Kb3dModel<KbQuaternion>({
    token: Token.KbQuaternion,
    trackChanges: false,
    create: args => new KbQuaternion(args.x || 0, args.y || 0, args.z || 0, args.w || 1),
})
export class KbQuaternion implements IKbQuaternion {
    constructor(
        @Ignore() readonly x: number,
        @Ignore() readonly y: number,
        @Ignore() readonly z: number,
        @Ignore() readonly w: number
    ) {
        if (this.w > 1) {
            this.w = 1;
        }
        if (this.w < -1) {
            this.w = -1;
        }
    }

    clone() {
        return new KbQuaternion(this.x, this.y, this.z, this.w);
    }

    assign(v: { x?: number | null; y?: number | null; z?: number | null; w?: number | null }) {
        return new KbQuaternion(
            typeof v.x === 'number' ? v.x : this.x,
            typeof v.y === 'number' ? v.y : this.y,
            typeof v.z === 'number' ? v.z : this.z,
            typeof v.w === 'number' ? v.w : this.w
        );
    }

    toEuler(): KbVector {
        let x: number, y: number, z: number;

        const qz = this.z;
        const qx = this.x;
        const qy = this.y;
        const qw = this.w;
        const sqw = qw * qw;
        const sqz = qz * qz;
        const sqx = qx * qx;
        const sqy = qy * qy;
        const zAxisY = qy * qz - qx * qw;
        const limit = 0.4999999;
        if (zAxisY < -limit) {
            y = 2 * Math.atan2(qy, qw);
            x = Math.PI / 2;
            z = 0;
        } else if (zAxisY > limit) {
            y = 2 * Math.atan2(qy, qw);
            x = -Math.PI / 2;
            z = 0;
        } else {
            z = Math.atan2(2.0 * (qx * qy + qz * qw), -sqz - sqx + sqy + sqw);
            x = Math.asin(-2.0 * (qz * qy - qx * qw));
            y = Math.atan2(2.0 * (qz * qx + qy * qw), sqz - sqx - sqy + sqw);
        }

        const scalar = 180 / Math.PI;

        return new KbVector(x * scalar, y * scalar, z * scalar);
    }

    multiply(q: { x: number; y: number; z: number; w: number }) {
        const x = this.x * q.w + this.y * q.z - this.z * q.y + this.w * q.x;
        const y = -this.x * q.z + this.y * q.w + this.z * q.x + this.w * q.y;
        const z = this.x * q.y - this.y * q.x + this.z * q.w + this.w * q.z;
        const w = -this.x * q.x - this.y * q.y - this.z * q.z + this.w * q.w;

        return new KbQuaternion(x, y, z, w);
    }

    equal(q: IKbQuaternionLike | undefined): boolean {
        return (
            !!q &&
            basicallyEqual(q.x, this.x) &&
            basicallyEqual(q.y, this.y) &&
            basicallyEqual(q.z, this.z) &&
            basicallyEqual(q.w, this.w)
        );
    }

    toObject() {
        return { x: this.x, y: this.y, z: this.z, w: this.w } as IKbQuaternionLike;
    }

    getAngle() {
        return this._getRadAngle() * (180 / Math.PI);
    }

    private _getRadAngle() {
        return Math.acos(this.w) * 2;
    }

    getAxis() {
        const angle = this._getRadAngle();
        const sin = Math.sin(angle / 2);

        if (sin === 0) {
            return KbVector.Zero();
        } else {
            return new KbVector(this.x / sin, this.y / sin, this.z / sin).normalize();
        }
    }

    static FromRotationAxis(axis: KbVector, angle: number) {
        // var sin = Math.sin(angle / 2);
        // axis.normalize();
        // result.w = Math.cos(angle / 2);
        // result.x = axis._x * sin;
        // result.y = axis._y * sin;
        // result.z = axis._z * sin;

        const rad = (angle * Math.PI) / 180;
        const sin = Math.sin(rad / 2);
        const nAxis = axis.normalize();
        const result = new KbQuaternion(nAxis.x * sin, nAxis.y * sin, nAxis.z * sin, Math.cos(rad / 2));
        return result;
    }

    static FromYawPitchRoll(dV: KbVector) {
        const v = dV.scale(Math.PI / 180);
        const halfRoll = v.z * 0.5;
        const halfPitch = v.x * 0.5;
        const halfYaw = v.y * 0.5;
        const sinRoll = Math.sin(halfRoll);
        const cosRoll = Math.cos(halfRoll);
        const sinPitch = Math.sin(halfPitch);
        const cosPitch = Math.cos(halfPitch);
        const sinYaw = Math.sin(halfYaw);
        const cosYaw = Math.cos(halfYaw);
        const result = new KbQuaternion(
            cosYaw * sinPitch * cosRoll + sinYaw * cosPitch * sinRoll,
            sinYaw * cosPitch * cosRoll - cosYaw * sinPitch * sinRoll,
            cosYaw * cosPitch * sinRoll - sinYaw * sinPitch * cosRoll,
            cosYaw * cosPitch * cosRoll + sinYaw * sinPitch * sinRoll
        );

        return result;
    }

    static Identity() {
        return new KbQuaternion(0, 0, 0, 1);
    }

    static From(q: IKbQuaternionLike) {
        return new KbQuaternion(q.x, q.y, q.z, q.w);
    }
}

function basicallyEqual(a: number, b: number) {
    return Math.abs(a - b) < 0.0000000001; // To deal with floating point errors
}
